import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import {
  ThemeProvider,
  CssBaseline,
  createTheme,
  darkTheme,
  brandColors,
} from "@digitalreality/ui";
import "./i18n";
import { StateProvider } from "./StateStore/Store";
import { leicaColors, pangeaUIColors } from "./Helpers/Colors";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import Dashboard from "./Pages/Dashboard/Dashboard";
import Licenses from "./Pages/Licenses/Licenses";
import Error from "./Pages/Error";
import ClientsList from "./Pages/Client/ClientsList";
import Login from "./Pages/Login";
import App from "./App";

var currentTheme = darkTheme;

const pangeaUITypography = {
  caption: {
    fontWeight: 400,
    fontSize: "12px",
    lineHeight: "16px",
  },
  body: {
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "20px",
  },
  bodyStrong: {
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "20px",
  },
  bodyLarge: {
    fontWeight: 400,
    fontSize: "18px",
    lineHeight: "24px",
  },
  bodyLargeStrong: {
    fontWeight: 500,
    fontSize: "18px",
    lineHeight: "24px",
  },
  subtitle: {
    fontWeight: 400,
    fontSize: "20px",
    lineHeight: "28px",
  },
  title: {
    fontWeight: 400,
    fontSize: "28px",
    lineHeight: "36px",
  },
  titleLarge: {
    fontWeight: 400,
    fontSize: "40px",
    lineHeight: "52px",
  },
};

export const customDarkTheme = createTheme({
  ...currentTheme,
  typography: {
    ...currentTheme.typography,
    fontSize: 14,
    body1: {
      fontWeight: pangeaUITypography.body.fontWeight,
      fontSize: pangeaUITypography.body.fontSize,
      lineHeight: pangeaUITypography.body.lineHeight,
    },
    title: {
      fontWeight: pangeaUITypography.title.fontWeight,
      fontSize: pangeaUITypography.title.fontSize,
      lineHeight: pangeaUITypography.title.lineHeight,
    },
    caption: {
      fontWeight: pangeaUITypography.caption.fontWeight,
      fontSize: pangeaUITypography.caption.fontSize,
      lineHeight: pangeaUITypography.caption.lineHeight,
    },
  },
  palette: {
    ...currentTheme.palette,
    background: {
      default: pangeaUIColors.grey[850],
      paper: pangeaUIColors.grey[900],
    },
    secondary: {
      main: leicaColors[200],
      light: leicaColors[100],
      dark: leicaColors[300],
    },
    divider:
      currentTheme.palette.mode === "dark"
        ? pangeaUIColors.whiteOp[900]
        : pangeaUIColors.blackOp[300],
  },
  colors: {
    ...currentTheme.colors,
    secondary: brandColors.leica,
    ui: {
      ...currentTheme.colors.ui,
      modal: pangeaUIColors.grey.darkGrey, // actually menu
    },
  },
  components: {
    ...currentTheme.components,
    MuiDialog: {
      ...currentTheme.components?.MuiDialog,
      styleOverrides: {
        root: {
          ".MuiDialog-paper": {
            backgroundColor: pangeaUIColors.grey[900],
            backgroundImage: "none",
            borderRadius: "15px",
          },
          ".MuiDialog-paper > .MuiIconButton-root": {
            width: "40px",
            height: "40px",
            color:
              currentTheme.palette.mode === "dark"
                ? pangeaUIColors.grey[350]
                : pangeaUIColors.grey[700],
          },
        },
      },
    },
    MuiDialogTitle: {
      ...currentTheme.components?.MuiDialogTitle,
      styleOverrides: {
        root: {
          fontSize: pangeaUITypography.title.fontSize,
          fontWeight: pangeaUITypography.title.fontWeight,
          lineHeight: pangeaUITypography.title.lineHeight,
        },
      },
    },
    MuiDialogContent: {
      ...currentTheme.components?.MuiDialogContent,
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.dividers && {
            borderTop: "none",
            borderBottom: "none",
          }),
        }),
      },
    },
    MuiDialogActions: {
      ...currentTheme.components?.MuiDialogActions,
      // Modal footer padding adjustment
      styleOverrides: {
        root: {
          background: "none !important",
        },
      },
    },
    MuiButton: {
      ...currentTheme.components?.MuiButton,
      styleOverrides: {
        ...currentTheme.components?.MuiButton?.styleOverrides,
        sizeMedium: ({ theme }) => ({
          height: "40px",
          padding: "0px 12px",
          fontSize: pangeaUITypography.body.fontSize,
          fontWeight: pangeaUITypography.body.fontWeight,
          lineHeight: pangeaUITypography.body.lineHeight,
        }),
        textSizeMedium: ({ theme }) => ({
          fontSize: pangeaUITypography.body.fontSize,
          lineHeight: pangeaUITypography.body.lineHeight,
        }),
        sizeLarge: ({ theme }) => ({
          height: "48px",
          padding: "0px 20px",
          fontSize: pangeaUITypography.body.fontSize,
          fontWeight: pangeaUITypography.body.fontWeight,
          lineHeight: pangeaUITypography.body.lineHeight,
        }),
        textSizeLarge: ({ theme }) => ({
          fontSize: pangeaUITypography.body.fontSize,
          lineHeight: pangeaUITypography.body.lineHeight,
        }),
      },
    },
    MuiTabs: {
      ...currentTheme.components?.MuiTabs,
      styleOverrides: {
        flexContainer: {
          justifyContent: "space-between",
        },
      },
    },
    MuiTab: {
      ...currentTheme.components?.MuiTab,
      styleOverrides: {
        root: {
          fontSize: pangeaUITypography.body.fontSize,
          fontWeight: pangeaUITypography.body.fontWeight,
          lineHeight: pangeaUITypography.body.lineHeight,
          paddingLeft: "0px",
          paddingRight: "0px",
          marginRight: "20px",
          minWidth: "20px",
          "&.Mui-selected": {
            color:
              currentTheme.palette.mode === "dark"
                ? pangeaUIColors.white
                : pangeaUIColors.black,
          },
          "&.Mui-disabled": {
            color:
              currentTheme.palette.mode === "dark"
                ? pangeaUIColors.whiteOp[700]
                : pangeaUIColors.grey[700],
          },
        },
      },
    },
    MuiFormControl: {
      ...currentTheme.components?.MuiFormControl,
      styleOverrides: {
        root: {
          width: "250px",
        },
      },
    },
    MuiAvatar: {
      ...currentTheme.components?.MuiAvatar,
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.dark,
          width: "34px",
          height: "34px",
          fontSize: "1rem",
          border: "none",
          borderRadius: "6px",
          top: "0px",
        }),
      },
    },
  },
});

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    errorElement: <Error />,
    children: [
      { index: true, element: <ClientsList /> },
      { path: "dashboard", element: <Dashboard /> },
      { path: "licenses", element: <Licenses /> },
      { path: "projects", element: <ClientsList /> },
    ],
  },
  {
    path: "login",
    element: <Login />,
  },
  {
    path: "*",
    element: <Login />,
  },
]);
root.render(
  <React.StrictMode>
    <StateProvider>
      <ThemeProvider themeOverwrite={customDarkTheme}>
        <CssBaseline />
        <RouterProvider router={router} />
      </ThemeProvider>
    </StateProvider>
  </React.StrictMode>
);
